import { Import, LogOut, Settings, User, type LucideIcon } from "lucide-react";
import {
  useGetIdentity,
  usePermissions,
  useLogout,
  useTranslate,
  useUserMenu,
} from "ra-core";
import { Link } from "react-router";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { releaseDialerOnSignOut } from "../dialer/releaseOnSignOut";
import { IS_DEMO } from "../env";
import { DuplicatesCount, DuplicatesDot } from "../dataQuality/DuplicatesCount"; // GAVION import duplicates
import { settingsLinks, type SettingsLink } from "./navItems";

const MenuLink = ({
  to,
  icon: Icon,
  label,
  badge,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  badge?: SettingsLink["badge"];
}) => {
  const translate = useTranslate();
  const ctx = useUserMenu();
  return (
    <DropdownMenuItem asChild onClick={ctx?.onClose}>
      <Link to={to} className="flex items-center gap-2">
        <Icon aria-hidden="true" />
        {translate(label)}
        {badge === "data_quality_duplicates" && <DuplicatesCount />}
      </Link>
    </DropdownMenuItem>
  );
};

const SignOutEverywhere = () => {
  const translate = useTranslate();
  const logout = useLogout();
  const { identity } = useGetIdentity();
  const onClick = async () => {
    if (!IS_DEMO) {
      // GAVION: B2-I2 release the dialer lease while the token is still valid (spec 04 §2.4)
      await releaseDialerOnSignOut(getSupabaseClient(), identity?.id);
      await getSupabaseClient().auth.signOut({ scope: "global" });
    }
    logout();
  };
  return (
    <DropdownMenuItem onClick={onClick} className="flex items-center gap-2">
      <LogOut aria-hidden="true" />
      {translate("gavion.common.sign_out_all")}
    </DropdownMenuItem>
  );
};

/** Mon profil, Intégrations, Se déconnecter partout; UserMenu itself adds "Se déconnecter" (spec 10 §4). */
const AdminLinks = () => {
  const { permissions } = usePermissions();
  if (permissions !== "admin") return null;
  return (
    <>
      <MenuLink
        to="/settings"
        icon={Settings}
        label="gavion.nav.configuration"
      />
      <MenuLink to="/import" icon={Import} label="gavion.nav.import_data" />
    </>
  );
};

/** Batch 2 Paramètres pages: Téléphone and Automatisations for all, Appels and Santé for admins. */
const SettingsLinks = () => {
  const { permissions } = usePermissions();
  return (
    <>
      {settingsLinks(permissions === "admin").map((l) => (
        <MenuLink
          key={l.to}
          to={l.to}
          icon={l.icon}
          label={l.labelKey}
          badge={l.badge}
        />
      ))}
    </>
  );
};

export const GavionUserMenu = () => {
  const translate = useTranslate();
  return (
    <UserMenu
      label={translate("gavion.nav.user_menu")}
      triggerBadge={<DuplicatesDot className="-right-0.5 -top-0.5" />}
    >
      <MenuLink to="/profile" icon={User} label="gavion.nav.my_profile" />
      <AdminLinks />
      <SettingsLinks />
      <SignOutEverywhere />
    </UserMenu>
  );
};
